import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Copy } from "@phosphor-icons/react";
import { useTheme } from "./ThemeProvider";
import { cn } from "@/lib/utils";

export function Eyebrow({ icon: Icon, children, className }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-2 rounded-full bg-accent px-4 py-1.5 text-sm font-bold uppercase tracking-wider text-accent-foreground",
        className,
      )}
    >
      {Icon && <Icon size={16} weight="bold" />}
      {children}
    </span>
  );
}

export function SectionShell({ id, eyebrow, eyebrowIcon, title, description, children }) {
  return (
    <motion.section
      id={id}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, ease: [0.34, 1.56, 0.64, 1] }}
      className="scroll-mt-24 border-t border-border/70 py-16 md:py-24"
      data-testid={`section-${id}`}
    >
      <div className="mb-10 max-w-2xl md:mb-14">
        {eyebrow && <Eyebrow icon={eyebrowIcon}>{eyebrow}</Eyebrow>}
        <h2 className="mt-5 text-3xl font-display font-extrabold tracking-tight md:text-4xl">{title}</h2>
        {description && (
          <p className="mt-4 text-lg font-medium leading-relaxed text-muted-foreground text-balance">{description}</p>
        )}
      </div>
      {children}
    </motion.section>
  );
}

export function Panel({ className, children, ...props }) {
  return (
    <div
      className={cn("rounded-3xl border border-border bg-card p-6 shadow-soft md:p-8", className)}
      {...props}
    >
      {children}
    </div>
  );
}

export function ColorSwatch({ swatch }) {
  const { theme } = useTheme();
  const [copied, setCopied] = useState(false);
  const hex = theme === "dark" ? swatch.dark : swatch.light;
  const textCls = theme === "dark" ? swatch.onDark : swatch.onLight;

  const copy = () => {
    navigator.clipboard?.writeText(hex);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  return (
    <button
      type="button"
      onClick={copy}
      data-testid={`swatch-${swatch.varName.replace(/--/, "")}`}
      className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card text-left shadow-soft transition-all duration-300 ease-bounce hover:-translate-y-1 hover:shadow-soft-lg focus-visible:outline-none"
    >
      <div className={cn("relative flex h-24 items-end justify-between p-3", textCls)} style={{ backgroundColor: hex }}>
        <span className="text-xs font-bold uppercase tracking-wider opacity-90">{hex}</span>
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/25 opacity-0 backdrop-blur transition-opacity group-hover:opacity-100">
          {copied ? <Check size={14} weight="bold" /> : <Copy size={14} weight="bold" />}
        </span>
      </div>
      <div className="p-3">
        <p className="text-sm font-bold leading-tight">{swatch.name}</p>
        <p className="mt-0.5 text-xs font-semibold text-muted-foreground">{swatch.role}</p>
      </div>
    </button>
  );
}

export function TokenChip({ children }) {
  return (
    <code className="rounded-lg bg-muted px-2 py-1 font-mono text-xs font-semibold text-foreground/80">
      {children}
    </code>
  );
}
